import { useCallback, useEffect, useRef } from 'react';
import useMatchMedia from 'hooks/useMatchMedia';

const TOLLBAR_HEIGHT = 48;
const FILES_HEIGHT = 64;
const ACTIONS_HEIGHT = 64;
const MESSAGE_MOBILE_HEIGHT = 120;

const BASE_FORM_HEIGHT =
  MESSAGE_MOBILE_HEIGHT + ACTIONS_HEIGHT + TOLLBAR_HEIGHT;

const getFormHeight = (hasFiles: boolean) =>
  hasFiles ? BASE_FORM_HEIGHT + FILES_HEIGHT : BASE_FORM_HEIGHT;

export default function useRichTextEditorStyles(
  disabled: boolean,
  hasFiles: boolean,
) {
  const formRef = useRef(null);
  const richTextRef = useRef(null);
  const isInsideOfRichtext = useRef(false);

  const isPhablet = useMatchMedia('isPhablet');

  const onFocus = useCallback(() => {
    const form = formRef?.current;
    const richTextWrapper = richTextRef?.current;
    const parentEl = form?.parentElement;

    if (!isPhablet || !form || !richTextWrapper || !parentEl || disabled)
      return;

    const siblingDiv = form?.previousElementSibling;
    const richTextTexArea = richTextWrapper.getElementsByClassName(
      'anys-rich-text-editor__textarea',
    )[0];

    isInsideOfRichtext.current = true;

    form.style.height = '100%';

    const richTextTexAreaHeight = `calc(100% - ${TOLLBAR_HEIGHT}px)`;

    richTextTexArea.style.setProperty(
      'max-height',
      richTextTexAreaHeight,
      'important',
    );
    richTextTexArea.style.setProperty(
      'min-height',
      richTextTexAreaHeight,
      'important',
    );

    // Add sibling hiddding styles
    siblingDiv.style.height = '0';
    siblingDiv.style.paddingBottom = '0';
    siblingDiv.style.paddingTop = '0';
  }, [disabled, isPhablet]);

  const onBlur = useCallback(() => {
    const form = formRef?.current;
    const richTextWrapper = richTextRef?.current;
    const parentEl = form?.parentElement;

    if (!isPhablet || !form || !richTextWrapper || !parentEl || disabled)
      return;

    const siblingDiv = form?.previousElementSibling;
    const richTextTexArea = richTextWrapper.getElementsByClassName(
      'anys-rich-text-editor__textarea',
    )[0];

    isInsideOfRichtext.current = false;

    form.style.height = `${getFormHeight(hasFiles)}px`;

    const richTextTexAreaHeight = `${MESSAGE_MOBILE_HEIGHT}px`;

    richTextTexArea.style.setProperty(
      'max-height',
      richTextTexAreaHeight,
      'important',
    );
    richTextTexArea.style.setProperty(
      'min-height',
      richTextTexAreaHeight,
      'important',
    );

    // Remove sibling hidding styles
    siblingDiv.style.height = '';
    siblingDiv.style.paddingBottom = '';
    siblingDiv.style.paddingTop = '';
  }, [disabled, hasFiles, isPhablet]);

  useEffect(() => {
    const richTextWrapper = richTextRef?.current;

    if (!isPhablet || !richTextWrapper || disabled) return;

    // setting richtext height based on files

    const richTextEditor = richTextWrapper.getElementsByClassName(
      'anys-rich-text-editor',
    )[0];

    const removeHeight = hasFiles ? FILES_HEIGHT : 0;

    const richTextEditorHeight = `calc(100% - ${removeHeight}px)`;

    richTextEditor.style.setProperty(
      'max-height',
      richTextEditorHeight,
      'important',
    );
    richTextEditor.style.setProperty(
      'min-height',
      richTextEditorHeight,
      'important',
    );
  }, [disabled, hasFiles, isPhablet]);

  useEffect(() => {
    const form = formRef?.current;

    if (!isPhablet || !form || disabled || isInsideOfRichtext?.current) return;

    // recalculating height if hasFiles change outside of form

    form.style.height = `${getFormHeight(hasFiles)}px`;
  }, [disabled, hasFiles, isPhablet]);

  useEffect(() => {
    const handleBodyClick = (event: MouseEvent) => {
      const form = formRef?.current;
      const richTextWrapper = richTextRef?.current;
      const parentEl = form?.parentElement;

      if (!isPhablet || !form || !richTextWrapper || !parentEl || disabled)
        return;

      const clickedInside = richTextWrapper.contains(event.target);

      if (clickedInside) {
        onFocus();
      } else {
        onBlur();
      }
    };

    document.body.addEventListener('click', handleBodyClick);

    return () => {
      document.body.removeEventListener('click', handleBodyClick);
    };
  }, [disabled, isPhablet, onBlur, onFocus]);

  useEffect(() => {
    if (isInsideOfRichtext.current) {
      onFocus();
    } else {
      onBlur();
    }
  }, [onBlur, onFocus]);

  return { formRef, richTextRef };
}
